/**
 * Panel definitions for flex/mfrr segment
 * Add your mfrr-related panels here based on backend dataset configs
 */
import moment from 'moment-timezone';
import { applyCountryFilterToPanels } from './countryContext';

const SUPPLY_CURVE_DATASET_ID = 'entsoe-procured-balancing-capacity';
const SUPPLY_CURVE_TIMEZONE = 'Europe/Amsterdam';
const SUPPLY_CURVE_START_Y = 22;
const SUPPLY_CURVE_PANEL_HEIGHT = 15;
const SUPPLY_CURVE_PANEL_WIDTH = 12;

// mFRR(da) is procured as a single full-day (24h) product in the markets we
// default to (e.g. NL) — there are no 4-hour blocks — so default to the full-day
// delivery window matching observed_time_horizon=24h.
function defaultDeliveryWindow(dayOffset) {
  const start = moment.tz(SUPPLY_CURVE_TIMEZONE).startOf('day').add(dayOffset, 'days');
  const end = start.clone().add(24, 'hours');
  return [`${start.format()}/${end.format()}`];
}

function supplyCurvePanel({ direction, label, x }) {
  return {
    id: `mfrrda-supply-today-${direction}`,
    type: 'datasetChart',
    datasetId: SUPPLY_CURVE_DATASET_ID,
    visualizationId: 'entsoe-procured-balancing-capacity-mfrrda-day-supply-curves',
    labelOverrides: {
      title: `Incident Reserves aanbodcurve - ${label}`,
      descriptionKey: 'dashboard_panels.procured_supply_curve_subtitle',
    },
    controlOverrides: {
      filters: {
        reserve_product: 'mfrr',
        balance_direction: direction,
        observed_time_horizon: '24h',
        delivery_window: defaultDeliveryWindow(0),
      },
      hiddenFilters: ['balance_direction', 'country', 'reserve_product'],
      visibility: {
        filters: true,
        dateRange: false,
        granularity: false,
      },
    },
    gridPos: {
      x,
      y: SUPPLY_CURVE_START_Y,
      w: SUPPLY_CURVE_PANEL_WIDTH,
      h: SUPPLY_CURVE_PANEL_HEIGHT,
    },
  };
}

const supplyCurvePanels = [
  supplyCurvePanel({ direction: 'up', label: 'Opregelen', x: 0 }),
  supplyCurvePanel({ direction: 'down', label: 'Afregelen', x: 12 }),
];

const lowerRowY = SUPPLY_CURVE_START_Y + SUPPLY_CURVE_PANEL_HEIGHT;

const basePanels = [
  // First row: yesterday / today / tomorrow (3 panels, 8 cols each)
  {
    id: 'mfrr-prices-yesterday',
    type: 'datasetChart',
    datasetId: 'entsoe-mfrr',
    visualizationId: 'entsoe-mfrr-mfrr-yesterday',
    gridPos: { x: 0, y: 0, w: 8, h: 10 },
  },
  {
    id: 'mfrr-prices-today',
    type: 'datasetChart',
    datasetId: 'entsoe-mfrr',
    visualizationId: 'entsoe-mfrr-mfrr-today',
    gridPos: { x: 8, y: 0, w: 8, h: 10 },
  },
  {
    id: 'mfrr-prices-tomorrow',
    type: 'datasetChart',
    datasetId: 'entsoe-mfrr',
    visualizationId: 'entsoe-mfrr-mfrr-tomorrow',
    gridPos: { x: 16, y: 0, w: 8, h: 10 },
  },

  // Second row: last 7 days + weekly (2 panels, 12 cols each)
  {
    id: 'mfrr-volumes-last-7d',
    type: 'datasetChart',
    datasetId: 'entsoe-mfrr',
    visualizationId: 'entsoe-mfrr-chart',
    gridPos: { x: 0, y: 10, w: 12, h: 12 },
  },
  {
    id: 'mfrr-volumes-by-week',
    type: 'datasetChart',
    datasetId: 'entsoe-mfrr',
    visualizationId: 'entsoe-mfrr-mfrr-by-week-chart',
    gridPos: { x: 12, y: 10, w: 12, h: 12 },
  },
  // Third row: today's procured capacity supply curves by direction.
  ...supplyCurvePanels,
  {
    id: 'mfrr-volumes-by-month',
    type: 'datasetChart',
    datasetId: 'entsoe-mfrr',
    visualizationId: 'entsoe-mfrr-mfrr-by-month-chart',
    gridPos: { x: 0, y: lowerRowY, w: 12, h: 15 },
  },
  {
    id: 'mfrr-activated-prices',
    type: 'datasetChart',
    datasetId: 'entsoe-activated-prices',
    visualizationId: 'entsoe-activated-prices-mfrr-chart',
    gridPos: { x: 12, y: lowerRowY, w: 12, h: 15 },
  },
];

export const flexMfrrPanels = ({ country } = {}) => applyCountryFilterToPanels(basePanels, country);

export const flexMfrrSyncGroups = [
  {
    id: 'mfrr-prices-row',
    global: false,
    ids: ['mfrr-prices-yesterday', 'mfrr-prices-today', 'mfrr-prices-tomorrow'],
    syncAxes: { y: true },
  },
];
